import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { HRService } from '@/services/hr.service';
import { EmployeeStatus } from '@/models/hr.model';
import { format } from 'date-fns';
import { Button, Card, Badge, LoadingSpinner, EmptyState } from '@/components/common';
import { User, ArrowLeft, Edit, Trash2, Mail, Phone, Briefcase, Calendar, DollarSign, Clock, CalendarDays } from 'lucide-react';

export const EmployeeDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: employee, isLoading } = useQuery({
    queryKey: ['employee', id],
    queryFn: () => id ? HRService.getEmployeeById(id) : null,
    enabled: !!id,
  });

  const { data: timeOffRequests = [] } = useQuery({
    queryKey: ['timeOffRequests', id],
    queryFn: () => id ? HRService.getTimeOffRequestsByEmployee(id) : [],
    enabled: !!id,
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      // Note: We don't have delete in HRService yet, but we can update status to terminated
      if (id) {
        await HRService.updateEmployee(id, { status: EmployeeStatus.terminated });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      navigate('/hr');
    },
  });

  if (isLoading) {
    return <LoadingSpinner text="Loading employee details..." fullScreen />;
  }

  if (!employee) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <EmptyState
          icon={User}
          title="Employee not found"
          description="The employee you're looking for doesn't exist."
          action={
            <Button variant="primary" icon={ArrowLeft} onClick={() => navigate('/hr')}>
              Back to HR
            </Button>
          }
        />
      </div>
    );
  }

  const getStatusVariant = (status: string): "success" | "warning" | "danger" | "gray" => {
    const variants: Record<string, "success" | "warning" | "danger" | "gray"> = {
      active: 'success',
      on_leave: 'warning',
      terminated: 'danger',
      pending: 'gray',
    };
    return variants[status] || 'gray';
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <Button
          variant="ghost"
          icon={ArrowLeft}
          onClick={() => navigate('/hr')}
          className="mb-4"
        >
          Back to HR
        </Button>
        <Card>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-green-100 to-green-200 flex items-center justify-center">
                <User className="w-8 h-8 text-green-600" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{employee.fullName}</h1>
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <Badge variant="info" size="sm">{employee.role}</Badge>
                  <Badge variant="info" size="sm">{employee.department}</Badge>
                  <Badge variant={getStatusVariant(employee.status)} size="sm">
                    {employee.status}
                  </Badge>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="primary"
                size="sm"
                icon={Edit}
                onClick={() => navigate(`/hr/employees/${id}/edit`)}
              >
                Edit
              </Button>
              <Button
                variant="danger"
                size="sm"
                icon={Trash2}
                onClick={() => {
                  if (confirm('Are you sure you want to terminate this employee?')) {
                    deleteMutation.mutate();
                  }
                }}
              >
                Terminate
              </Button>
            </div>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2 space-y-6">
          <Card>
            <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
              <User className="w-5 h-5 text-blue-600" />
              Employee Information
            </h2>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <dt className="text-sm font-medium text-gray-500 mb-1 flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Email
                </dt>
                <dd className="text-sm text-gray-900">{employee.email}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 mb-1 flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Phone
                </dt>
                <dd className="text-sm text-gray-900">{employee.phoneNumber || 'N/A'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 mb-1 flex items-center gap-2">
                  <Briefcase className="w-4 h-4" />
                  Employment Type
                </dt>
                <dd className="text-sm text-gray-900 capitalize">{employee.employmentType.replace('_', ' ')}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 mb-1 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Start Date
                </dt>
                <dd className="text-sm text-gray-900">{format(employee.startDate, 'MMM d, yyyy')}</dd>
              </div>
              {employee.salary && (
                <div>
                  <dt className="text-sm font-medium text-gray-500 mb-1 flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    Salary
                  </dt>
                  <dd className="text-sm text-gray-900 font-semibold">${employee.salary.toLocaleString()}/year</dd>
                </div>
              )}
              {employee.hourlyRate && (
                <div>
                  <dt className="text-sm font-medium text-gray-500 mb-1 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Hourly Rate
                  </dt>
                  <dd className="text-sm text-gray-900 font-semibold">${employee.hourlyRate.toFixed(2)}/hour</dd>
                </div>
              )}
            </dl>
          </Card>

          {timeOffRequests.length > 0 && (
            <Card>
              <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
                <CalendarDays className="w-5 h-5 text-blue-600" />
                Time Off Requests
              </h2>
              <div className="space-y-4">
                {timeOffRequests.map((request) => (
                  <div key={request.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{request.type}</p>
                      <p className="text-sm text-gray-500 mt-1">
                        {format(request.startDate, 'MMM d')} - {format(request.endDate, 'MMM d, yyyy')} • {request.days} days
                      </p>
                    </div>
                    <Badge variant={getStatusVariant(request.status)} size="sm">
                      {request.status}
                    </Badge>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>

        <div className="space-y-6">
          <Card>
            <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
            <div className="space-y-3">
              <Button
                variant="outline"
                fullWidth
                icon={CalendarDays}
                onClick={() => navigate(`/hr/timeoff/new?employeeId=${id}`)}
              >
                Request Time Off
              </Button>
              <Button
                variant="outline"
                fullWidth
                icon={DollarSign}
                onClick={() => navigate(`/payroll/employees/${id}`)}
              >
                View Payroll
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

